/**
 * useActivityLogs Hook
 * ดึงข้อมูล activity logs จาก API พร้อม filters และ pagination
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../services/api';

// Types
export interface ActivityLogUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

export interface ActivityLog {
  id: number;
  user_id?: string;
  action_type: string;
  entity_type?: string;
  entity_id?: string;
  description?: string;
  ip_address?: string;
  created_at: string;
  user?: ActivityLogUser;
}

export interface ActivityLogPagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface ActivityLogFilters {
  action_type?: string;
  entity_type?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface ActivityLogStats {
  today: number;
  this_week: number;
  this_month: number;
  most_active_users: {
    user_id: string;
    email: string;
    name: string;
    activity_count: number;
  }[];
  common_actions: {
    action_type: string;
    count: number;
  }[];
}

interface ActivityLogsResponse {
  success: boolean;
  data: ActivityLog[];
  pagination: ActivityLogPagination;
  filters: ActivityLogFilters;
}

interface ActivityStatsResponse {
  success: boolean;
  today: number;
  this_week: number;
  this_month: number;
  most_active_users: ActivityLogStats['most_active_users'];
  common_actions: ActivityLogStats['common_actions'];
}

interface UseActivityLogsOptions {
  page?: number;
  pageSize?: number;
  filters?: ActivityLogFilters;
  autoFetch?: boolean;
}

export const useActivityLogs = (options: UseActivityLogsOptions = {}) => {
  const {
    page: initialPage = 1,
    pageSize = 20,
    filters: initialFilters = {},
    autoFetch = true,
  } = options;

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [pagination, setPagination] = useState<ActivityLogPagination>({
    page: initialPage,
    page_size: pageSize,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [filters, setFilters] = useState<ActivityLogFilters>(initialFilters);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch activity logs
  const fetchLogs = useCallback(
    async (currentPage: number = initialPage, currentFilters: ActivityLogFilters = {}) => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          throw new Error('ไม่พบ token กรุณา login ใหม่');
        }

        // Build query params
        const params: any = {
          page: currentPage,
          page_size: pageSize,
        };

        // Add filters
        if (currentFilters.action_type) params.action_type = currentFilters.action_type;
        if (currentFilters.entity_type) params.entity_type = currentFilters.entity_type;
        if (currentFilters.user_id) params.user_id = currentFilters.user_id;
        if (currentFilters.date_from) params.date_from = currentFilters.date_from;
        if (currentFilters.date_to) params.date_to = currentFilters.date_to;
        if (currentFilters.search) params.search = currentFilters.search;

        const response = await axios.get<ActivityLogsResponse>(
          `${API_URL}/api/admin/activity-logs`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params,
          }
        );

        if (response.data.success) {
          setLogs(response.data.data);
          setPagination(response.data.pagination);
          setFilters(response.data.filters);
        } else {
          throw new Error('ไม่สามารถดึงข้อมูล activity logs ได้');
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.detail || err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล';
        setError(errorMessage);
        console.error('Error fetching activity logs:', err);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, initialPage]
  );

  // Fetch on mount if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchLogs(initialPage, initialFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch]);

  // Apply filters
  const applyFilters = useCallback(
    (newFilters: ActivityLogFilters) => {
      setFilters(newFilters);
      fetchLogs(1, newFilters); // Reset to page 1 when applying filters
    },
    [fetchLogs]
  );

  // Change page
  const goToPage = useCallback(
    (page: number) => {
      fetchLogs(page, filters);
    },
    [fetchLogs, filters]
  );

  // Refresh
  const refresh = useCallback(() => {
    fetchLogs(pagination.page, filters);
  }, [fetchLogs, pagination.page, filters]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({});
    fetchLogs(1, {});
  }, [fetchLogs]);

  return {
    logs,
    pagination,
    filters,
    loading,
    error,
    fetchLogs,
    applyFilters,
    goToPage,
    refresh,
    clearFilters,
  };
};

// Hook สำหรับดึงสถิติ activity logs
export const useActivityStats = () => {
  const [stats, setStats] = useState<ActivityLogStats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('ไม่พบ token กรุณา login ใหม่');
      }

      const response = await axios.get<ActivityStatsResponse>(
        `${API_URL}/api/admin/activity-logs/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setStats({
          today: response.data.today,
          this_week: response.data.this_week,
          this_month: response.data.this_month,
          most_active_users: response.data.most_active_users,
          common_actions: response.data.common_actions,
        });
      } else {
        throw new Error('ไม่สามารถดึงสถิติ activity logs ได้');
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail || err.message || 'เกิดข้อผิดพลาดในการดึงสถิติ';
      setError(errorMessage);
      console.error('Error fetching activity stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
  };
};
